import React, {
  FC,
  forwardRef,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  VFC,
} from 'react';

import classNames from 'classnames';
import { Icon, Text, Button, Interpolate } from 'mintel-react-ui';

import {
  BlockBackgroundsType,
  TableCellJson,
  TableContainerJson,
} from '/types';
import {
  findFirstExtraRowIndex,
  isViewedBlock,
  splitTableHeadAndBody,
} from '/utils/functions';
import useTranslate from '/utils/translations';

import styles from './TableColumn.scss';
import {
  BlockBackgroundContext,
  PresentModeContext,
} from '../../utils/contexts';

const TRUNCATED_TABLE_ROW_LIMIT = 15;

type Props = Pick<TableContainerJson, 'elements'> & {
  background: BlockBackgroundsType;
  hide?: boolean;
};

const Cell: FC<TableCellJson> = ({
  header,
  colspan,
  rowspan,
  alignment,
  children,
}) => {
  const props = {
    colSpan: colspan,
    rowSpan: rowspan,
  };
  if (header) {
    return (
      <th {...props} style={{ textAlign: alignment }}>
        {children}
      </th>
    );
  }
  return (
    <td {...props} style={{ textAlign: alignment }}>
      {children}
    </td>
  );
};

const Rows: VFC<{
  rows: TableCellJson[][];
  darkBg: boolean;
  zebra: boolean;
  hidden?: boolean;
}> = ({ rows, darkBg, zebra, hidden = false }) => (
  <>
    {rows.map((row, rowIndex) => (
      <tr
        key={rowIndex}
        className={classNames(styles.row, {
          [styles['hidden']]: hidden,
          [styles.zebra]: zebra,
        })}
      >
        {row.map((cell, colIndex) => (
          <Cell {...cell} key={colIndex}>
            <Text.Body darkBg={darkBg}>
              <span dangerouslySetInnerHTML={{ __html: cell.content }} />
            </Text.Body>
          </Cell>
        ))}
      </tr>
    ))}
  </>
);

const TableColumn = forwardRef<HTMLTableElement, Props>(
  ({ background, elements, hide = false }, ref) => {
    const [showLargeTable, setShowLargeTable] = useState(false);
    const [hasClickedButton, setHasClickedButton] = useState(false);
    const [isWidthOverflowing, setIsWidthOverflowing] = useState(false);
    const tableLoadButtonRef = useRef<HTMLDivElement>(null);
    const fadeRef = useRef<HTMLDivElement>(null);
    const presentMode = useContext(PresentModeContext);
    const { table, caption } = elements;
    const { darkBg } = useContext(BlockBackgroundContext);
    const t = useTranslate();
    const isLargeTable = table.data.length > TRUNCATED_TABLE_ROW_LIMIT;

    const [thead, tbody, tbodyExtra] = useMemo(() => {
      const [thead, tbody] = splitTableHeadAndBody(table.data);
      const tbodyExtra = tbody.splice(
        findFirstExtraRowIndex(tbody, TRUNCATED_TABLE_ROW_LIMIT - thead.length),
      );
      return [thead, tbody, tbodyExtra];
    }, [table.data]);

    useEffect(() => {
      if (!showLargeTable && !!tableLoadButtonRef.current) {
        const buttonInView = isViewedBlock(tableLoadButtonRef.current);
        if (!buttonInView && hasClickedButton) {
          tableLoadButtonRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
        }
      }
      setHasClickedButton(false);
    }, [showLargeTable, tableLoadButtonRef, hasClickedButton]);

    useEffect(() => {
      const tableDiv = fadeRef.current;
      const updateFade = (): void => {
        if (tableDiv) {
          setIsWidthOverflowing(
            tableDiv.scrollWidth - tableDiv.scrollLeft > tableDiv.clientWidth,
          );
        }
      };
      if (tableDiv) {
        tableDiv.addEventListener('scroll', updateFade);
        updateFade();
      }

      return (): void => {
        tableDiv?.removeEventListener('scroll', updateFade);
      };
    }, [fadeRef, setIsWidthOverflowing]);

    const lengthLabel = (
      <Interpolate
        template={t('Showing rows 1-{currentRows} of {totalRows}')}
        values={{
          currentRows: showLargeTable
            ? tbody.length + tbodyExtra.length
            : tbody.length,
          totalRows: tbody.length + tbodyExtra.length,
        }}
      />
    );

    return (
      <div
        className={classNames({
          [styles.hide]: hide,
          [styles['present-mode']]: !!presentMode,
        })}
      >
        {caption && !caption.hidden && caption.content && (
          <Text.Body darkBg={darkBg}>
            <div
              className={styles.caption}
              dangerouslySetInnerHTML={{
                __html: caption.content,
              }}
            />
          </Text.Body>
        )}
        {!table.hidden && (
          <div className={styles['fade-wrapper']}>
            <div className={styles['scroll-wrapper']} ref={fadeRef}>
              <table
                className={classNames(styles.table, styles[background], {
                  [styles[`${background}-fade`]]: isWidthOverflowing,
                })}
                ref={ref}
              >
                <thead>
                  <Rows rows={thead} darkBg={darkBg} zebra={false} />
                </thead>
                <tbody>
                  <Rows rows={tbody} darkBg={darkBg} zebra={true} />
                  <Rows
                    rows={tbodyExtra}
                    darkBg={darkBg}
                    zebra={true}
                    hidden={!!presentMode || !showLargeTable}
                  />
                </tbody>
              </table>
            </div>
          </div>
        )}
        {!table.hidden && isLargeTable && !presentMode && (
          <div ref={tableLoadButtonRef} className={styles['load-more']}>
            <Text.Body darkBg={darkBg}>{lengthLabel}</Text.Body>
            <Button.MutedButton
              handleClick={(): void => {
                setShowLargeTable(!showLargeTable);
                setHasClickedButton(true);
              }}
            >
              {showLargeTable ? t('Show less rows') : t('Show more rows')}
            </Button.MutedButton>
          </div>
        )}
        {!table.hidden && isLargeTable && presentMode && (
          <div className={styles.warning}>
            <Icon
              color={styles['warning-color']}
              name="warning_outline"
              size={16}
            />
            <Text.Body darkBg={darkBg}>
              {t('Exit presentation to view the full table. ')}
              {lengthLabel}
            </Text.Body>
          </div>
        )}
      </div>
    );
  },
);

TableColumn.displayName = 'TableColumn';
export default TableColumn;
